<template>
  <transition name="fade" appear>
    <div class="gallery">
      <div class="image-container">
        <div ref="swiper" class="swiper-container swiper-container">
          <div class="swiper-wrapper">
            <ShopByWidgetSlide
              @toggleProductTiles="toggleProductTiles"
              v-for="(item, index) in activeImages"
              ref="slides"
              :is-mobile="isMobile"
              :key="index"
              :data-index="index"
              :data-product-index="images.indexOf(item)"
              :index="index"
              :isActiveSlide="index === activeIndex"
              :item="item"
              :turn-off-all-experiments="turnOffAllExperiments"
              :tracking="{
                index: index,
                room: roomName,
                file: item.id,
                imageUrl: item.imageUrl,
                usedIn: item.assets,
                roomImagesNum: images.length,
                variant: imageTaggingVariant,
              }"
            />
          </div>

          <div v-if="carouselReady" class="swiper-pagination pagination"></div>

          <transition name="fade" appear>
            <div v-show="carouselReady" v-if="!swiper.isBeginning" class="swiper-button-prev" @click.passive="prevNaviClicked">
              <span class="af af-chevron-left"></span>
            </div>
          </transition>

          <transition name="fade" appear>
            <div v-show="carouselReady" class="swiper-button-next" @click.passive="nextNaviClicked"><span class="af af-chevron-right"></span></div>
          </transition>
        </div>
      </div>

      <div v-if="carouselReady" ref="tiles" class="product-tiles">
        <template v-if="showProductTiles">
          <transition-group name="staggered-fade" appear tag="div" @before-enter="beforeEnter" @enter="enter" @leave="leave">
            <ProductTile
              v-for="product in activeFileProducts"
              :key="product.id"
              :product="product"
              :data-key="index"
              :tracking="{
                index: index,
                room: roomName,
                file: activeImages[index].id,
                imageUrl: activeImages[index].imageUrl,
                usedIn: activeImages[index].assets,
                variant: imageTaggingVariant,
              }"
            />
          </transition-group>
        </template>
      </div>
    </div>
  </transition>
</template>

<script>
import smoothReflow from '@/plugins/smoothReflow';
import { debounce } from '@/components/commons/MiniLodash';
import ImportUtils from '@/components/commons/ImportUtils.js';
import LocalStorage, { LocalStorageKeys } from '@/components/commons/LocalStorage.js';
import ShopByWidgetSlide from './ShopByWidgetSlide.vue';
import Tracking from '@/components/commons/Tracking.js';
import { nextTick } from 'vue';
import ProductTile from './ProductTile.vue';
import { getImageTaggingTestVariant } from '@/components/commons/utils.js';
import useCommon from '@/compositions/useCommon';

export default {
  name: 'Gallery',
  components: {
    ShopByWidgetSlide,
    ProductTile,
  },
  mixins: [smoothReflow],
  props: {
    images: Array,
    roomName: String,
    isMobile: {
      type: Boolean,
      default: false,
    },
    turnOffAllExperiments: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      swiper: {
        animating: true,
      },
      index: 0,
      timer: null,
      activeIndex: 0,
      productIndex: 0,
      absIndex: 0,
      activeImages: [...this.images],
      showProductTiles: false,
      allImagesLoaded: true,
      imageTaggingVariant: getImageTaggingTestVariant(),
    };
  },

  computed: {
    carouselReady() {
      return this.allImagesLoaded;
    },

    lastSlideReached() {
      if (this.swiper) {
        return this.swiper.isEnd;
      }
      return false;
    },
  },

  setup() {
    const { activeFileProducts, updateCurrentRoom, updateRoomIdeasTracking, loadProductsForFile } = useCommon();
    const velocity = () => import('velocity-animate');
    return {
      activeFileProducts,
      updateCurrentRoom,
      updateRoomIdeasTracking,
      loadProductsForFile,
      velocity,
    };
  },

  mounted() {
    this.updateCurrentRoom(this.roomName);
    this.$smoothReflow({
      el: '.product-tiles',
      property: ['height', 'width'],
    });
    this.setSwiper();
    this.velocity().then((v) => {
      this.velocity = v.animate;
    });
  },

  methods: {
    async setSwiper() {
      const Swiper = await ImportUtils.getSwiper();

      this.swiper = new Swiper(this.$refs.swiper, {
        slidesPerView: 'auto',
        preloadImages: false,
        lazy: {
          loadPrevNext: true,
          loadPrevNextAmount: 4,
        },
        pagination: {
          el: '.swiper-pagination',
          dynamicBullets: true,
          type: 'bullets',
        },
        spaceBetween: 16,
        threshold: 15,
        watchSlidesVisibility: true,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
      });
      this.swiper.lazy.load();

      this.setActiveProducts();
      this.cloneSlide(true);

      this.swiper.on('lazyImageReady', debounce(this.updateSwiper, 200));

      // track image view for first image
      Tracking.track('room-ideas-image-loaded', { image_number: 0, id: this.images[0].id });
      const tracking = {
        image_number: this.index,
        room: this.roomName,
        room_images_num: this.images?.length,
        id: this.images[this.swiper?.slides[this.swiper.activeIndex]?.dataset?.productIndex]?.id,
        variant: this.imageTaggingVariant,
      };
      this.updateRoomIdeasTracking(tracking);

      this.swiper.on('slideChange', () => {
        tracking.image_number = this.index;
        tracking.id = this.images[this.swiper?.slides[this.swiper.activeIndex]?.dataset?.productIndex]?.id;
        this.updateRoomIdeasTracking(tracking);

        if (this.timer !== null) {
          clearTimeout(this.timer);
        }

        nextTick(() => {
          this.cloneSlide();
          const active = this.images[this.swiper.slides[this.swiper.activeIndex].dataset.productIndex];
          setTimeout(() => {
            Tracking.track('RoomIdeasImageView', {
              index: this.index,
              room: this.roomName,
              file: active.id,
              imageUrl: active.imageUrl,
              usedIn: active.assets,
            });
          }, 0);

          this.timer = setTimeout(() => {
            Tracking.track('image-tagging-image-view', {
              image_number: this.index,
              id: active.id,
              room: this.roomName,
              room_images_num: this.images.length,
              variant: this.imageTaggingVariant,
            });
          }, 1500);

          LocalStorage.set(LocalStorageKeys.ROOM_IDEAS_WIDGET_STATE, 'interacted');
        });
      });
    },

    cloneSlide(force = false) {
      this.productIndex = this.swiper.slides[this.swiper.activeIndex].dataset.productIndex;
      this.index = this.swiper.slides[this.swiper.activeIndex].dataset.index;

      if (this.swiper.activeIndex > this.absIndex || force) {
        this.activeImages = [...this.activeImages, this.images[this.productIndex]];
        this.absIndex = this.swiper.activeIndex;
      }

      this.updateSwiper();
    },

    setActiveProducts() {
      const { id } = this.images[this.productIndex];
      this.loadProductsForFile(id);
    },

    updateSwiper() {
      this.allImagesLoaded = true;
      nextTick(() => {
        this.swiper.update();
        // setTimeout(() => {
          this.activeIndex = this.swiper.activeIndex;
        // });
        this.setActiveProducts();
      });
    },

    prevNaviClicked() {
      this.swiper.slidePrev();
    },

    nextNaviClicked() {
      this.swiper.slideNext();
    },

    toggleProductTiles() {
      this.showProductTiles = !this.showProductTiles;
      if (this.showProductTiles) {
        setTimeout(() => {
          this.$refs.tiles.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 250);
      } else {
        this.$parent.$refs.widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },

    hideProductTiles() {
      if (this.showProductTiles) {
        this.toggleProductTiles();
      }
    },

    beforeEnter(el) {
      el.style.opacity = 0;
    },

    enter(el, done) {
      const delay = 150 + el.getAttribute('data-key') * 125;
      setTimeout(() => {
        this.velocity(el, { opacity: 1 }, { complete: done });
      }, delay);
    },

    leave(el, done) {
      const delay = 0;
      setTimeout(() => {
        this.velocity(el, { opacity: 0 }, { complete: done });
      }, delay);
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
/* purgecss start ignore */
@import 'swiper/dist/css/swiper.min.css';
/* purgecss end ignore */

.gallery {
  width: 100%;
  background: white;

  .image-container {
    width: 100%;
    height: 100%;
    position: relative;

    .swiper-container {
      height: 100%;
      width: 100%;
      position: relative;

      .swiper-button-prev,
      .swiper-button-next {
        left: 16px;
        visibility: hidden;
        opacity: 0;
        top: calc(50% - 50px / 2);
        margin-top: 0;
        height: 32px;
        width: 32px;
        padding: 8px;
        border-radius: 50%;
        background: white;
        box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.2);
        display: flex;
        justify-content: center;
        font-size: 1em;
        background-image: none;
        transition: visibility 0s linear 100ms, opacity 100ms ease-in;
        cursor: pointer;

        @media (max-width: calc($mobile-breakpoint - 1px)) {
          display: none;
        }

        &.swiper-button-disabled {
          display: none;
        }

        .af {
          padding: 10px;
          align-self: center;
          color: var(--link-color);
        }
      }

      .swiper-button-next {
        left: unset;
        right: 16px;
      }

      .swiper-wrapper {
        width: 100%;
        height: 100%;

        .swiper-slide {
          width: auto !important;
          height: 100%;
          background: white;
        }
      }
      /* purgecss ignore */
      &:hover {
        .swiper-button-next,
        .swiper-button-prev {
          visibility: visible;
          opacity: 1;
          transition: visibility 0s linear, opacity 200ms ease-out;

          &.swiper-button-disabled {
            pointer-events: initial;
            opacity: 50%;
          }
        }
      }
    }
  }
}

.pagination {
  z-index: 1;
  display: none;

  /* purgecss ignore */
  &.swiper-pagination.swiper-pagination-bullets.swiper-pagination-bullets-dynamic {
    position: relative;
    bottom: -4px;
    left: 50vw;
    margin-top: var(--space-1);
    margin-bottom: var(--space-4);
  }

  /* purgecss ignore */
  .swiper-pagination-bullet-active {
    background: $coral;
  }

  @media (max-width: calc($mobile-breakpoint - 1px)) {
    display: block;
  }
}

.product-tiles {
  position: relative;
  width: 100%;
  @media (max-width: calc($tablet-breakpoint - 1px)) {
    .mini-product.full-width {
      padding-left: 0;
      padding-right: 0;
    }
  }

  > div {
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    background: white;
    padding: 20px;

    @media (max-width: calc($tablet-breakpoint - 1px)) {
      padding: 20px 0;
    }
  }
}
</style>
